import React, { useMemo, useState } from 'react';
import invariant from 'tiny-invariant';
import { AvatarGroup, Box, Tooltip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { MoAvatar, SMALL_AVATAR_SIZE } from '@src/components/MoAvatar';
import { MoButton } from '@src/components/MoButton';
import ChatsHeader from './ChatsHeader';
import BackToChatsLink from './BackToChatsLink';
import EditChatModal from './EditChatModal';
import { useChatsContext } from '../ChatsContext';

const MAX_VISIBLE_PARTICIPANTS = 2;

type MessagesHeaderProps = {
  isActive: boolean;
  canEditChat: boolean;
};

const MessagesHeader = React.memo(function MessagesHeader({
  isActive,
  canEditChat,
}: MessagesHeaderProps) {
  const { chat } = useChatsContext();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  invariant(
    chat?.activeNonViewerParticipants,
    'MessagesHeader should only render with a loaded chat',
  );
  const { activeNonViewerParticipants } = chat;

  const isUserDeleted = activeNonViewerParticipants.some((participant) => participant.deletedAt);

  const allParticipantNames = useMemo(
    () => new Map(activeNonViewerParticipants.map((p) => [p.id, p.fullName])),
    [activeNonViewerParticipants],
  );

  const remainingCount = activeNonViewerParticipants.length - MAX_VISIBLE_PARTICIPANTS;
  const hasMoreParticipants = remainingCount > 0;

  const visibleNames = useMemo(() => {
    const participantNames = Array.from(allParticipantNames.values());
    if (!hasMoreParticipants) {
      return participantNames.join(', ');
    }
    return participantNames.slice(0, MAX_VISIBLE_PARTICIPANTS).join(', ');
  }, [allParticipantNames, hasMoreParticipants]);

  const tooltipContent = (
    <Box>
      {Array.from(allParticipantNames.entries()).map(([participantId, name]) => (
        <Box key={participantId} sx={{ fontWeight: 600 }}>
          {name}
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <ChatsHeader pl={4} pr={4} justifyContent="space-between">
        <Box display="flex" gap={2} alignItems="center" minWidth={0} flex={1}>
          <Box display={{ md: 'none' }}>
            <BackToChatsLink />
          </Box>
          <Box display={{ xs: 'none', md: 'block' }}>
            <AvatarGroup max={MAX_VISIBLE_PARTICIPANTS + 1}>
              {activeNonViewerParticipants.map((participant) => (
                <MoAvatar size={SMALL_AVATAR_SIZE} key={participant.id} user={participant} />
              ))}
            </AvatarGroup>
          </Box>
          <Typography
            variant="h3"
            color={!isActive || isUserDeleted ? 'grey.600' : 'inherit'}
            noWrap
            sx={{ minWidth: 0 }}
          >
            {visibleNames}
            {hasMoreParticipants && (
              <>
                ,{' '}
                <Tooltip
                  title={tooltipContent}
                  placement="bottom-start"
                  slotProps={{
                    tooltip: {
                      sx: {
                        px: 2,
                        py: 1.5,
                      },
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      '&::after': {
                        // eslint-disable-next-line motivo/prefer-smart-quotes
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: '0.15em', // Position relative to font size to align with text baseline
                        borderBottom: '1px dotted',
                        borderBottomColor: 'currentColor',
                      },
                    }}
                  >
                    and {remainingCount} more
                  </Box>
                </Tooltip>
              </>
            )}
            {(!isActive || isUserDeleted) && (
              <Box component="span" sx={{ typography: 'body1', ml: 1 }}>
                (Archived)
              </Box>
            )}
          </Typography>
        </Box>
        {canEditChat && (
          <MoButton
            size="small"
            onClick={() => setIsEditModalOpen(true)}
            startIcon={<EditOutlinedIcon />}
            sx={{ color: 'primary.main', flexShrink: 0 }}
          >
            Edit
          </MoButton>
        )}
      </ChatsHeader>
      {isEditModalOpen && <EditChatModal open onClose={() => setIsEditModalOpen(false)} />}
    </>
  );
});

export default MessagesHeader;
